import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiAssistantAPI, creditsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Brain,
  Send,
  Loader2,
  Plus,
  Trash2,
  MessageCircle,
  Sparkles,
  AlertCircle,
  Clock,
  ChevronLeft,
  Menu,
  X,
  Heart,
  Stethoscope,
  FileText,
  HelpCircle,
  Coins,
  ShoppingCart,
  Paperclip,
  Image,
  Mic,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import CreditsModal from '@/components/CreditsModal';

const SUGGESTED_QUESTIONS = [
  {
    icon: Heart,
    text: "J'ai des douleurs dans la poitrine, que dois-je faire ?",
    category: 'urgence'
  },
  {
    icon: Stethoscope,
    text: "Quels sont les symptômes de la grippe ?",
    category: 'symptomes'
  },
  {
    icon: FileText,
    text: "Comment dois-je prendre ce médicament ?",
    category: 'medicament'
  },
  {
    icon: HelpCircle,
    text: "Comment me préparer pour ma consultation ?",
    category: 'consultation'
  }
];

const AIAssistantPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [credits, setCredits] = useState({ balance: 0, loading: true });
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Credit costs for display
  const CREDIT_COSTS = {
    question: 1,
    file_analysis: 3,
    audio_analysis: 5
  };

  // Load sessions and credits on mount
  useEffect(() => {
    loadSessions();
    loadCredits();
  }, []);

  const loadCredits = async () => {
    try {
      const response = await creditsAPI.getBalance();
      setCredits({ balance: response.data.balance, loading: false });
    } catch (error) {
      console.error('Error loading credits:', error);
      setCredits({ balance: 0, loading: false });
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const response = await aiAssistantAPI.getSessions();
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSession = async (sid) => {
    try {
      setLoading(true);
      const response = await aiAssistantAPI.getHistory(sid);
      const sessionMessages = response.data.sessions[sid] || [];
      
      // Convert to message format and sort by date
      const formattedMessages = sessionMessages
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        .map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at
        }));
      
      setMessages(formattedMessages);
      setSessionId(sid);
      setShowSidebar(false);
    } catch (error) {
      toast.error('Erreur lors du chargement de la conversation');
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = () => {
    setMessages([]);
    setSessionId(null);
    setShowSidebar(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette conversation ?')) return;
    
    try {
      await aiAssistantAPI.deleteSession(sid);
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewSession();
      }
      toast.success('Conversation supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFileSelect = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    
    // Determine cost
    const isAudio = file.type.startsWith('audio/');
    const cost = isAudio ? CREDIT_COSTS.audio_analysis : CREDIT_COSTS.file_analysis;
    
    if (credits.balance < cost) {
      toast.error(`Crédits insuffisants. ${cost} crédits requis pour ce fichier.`);
      setShowCreditsModal(true);
      return;
    }
    
    setSelectedFile(file);
    setShowAttachMenu(false);
    toast.success(`Fichier sélectionné: ${file.name}`);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const getFileIcon = (file) => {
    if (!file) return null;
    if (file.type.startsWith('image/')) return <Image className="w-4 h-4" />;
    if (file.type.startsWith('audio/')) return <Mic className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const sendMessage = async (messageText = input) => {
    if ((!messageText.trim() && !selectedFile) || loading) return;
    
    // Determine required credits
    const requiredCredits = selectedFile 
      ? (selectedFile.type.startsWith('audio/') ? CREDIT_COSTS.audio_analysis : CREDIT_COSTS.file_analysis)
      : CREDIT_COSTS.question;
    
    // Check credits before sending
    if (credits.balance < requiredCredits) {
      setShowCreditsModal(true);
      toast.error(`Crédits insuffisants. ${requiredCredits} crédit${requiredCredits > 1 ? 's' : ''} requis.`);
      return;
    }
    
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText.trim() || 'Analyse ce fichier',
      timestamp: new Date().toISOString(),
      hasAttachment: !!selectedFile,
      attachmentName: selectedFile?.name,
      attachmentType: selectedFile?.type
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    
    const fileToSend = selectedFile;
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
    
    try {
      let response;
      
      if (fileToSend) {
        // Send with file
        response = await aiAssistantAPI.chatWithFile(
          messageText.trim() || 'Analyse ce fichier et décris ce que tu vois.',
          fileToSend,
          sessionId
        );
      } else {
        // Send text only
        response = await aiAssistantAPI.chat(messageText.trim(), sessionId);
      }
      
      // Check if response indicates insufficient credits
      if (response.data.error === 'insufficient_credits') {
        setShowCreditsModal(true);
        toast.error(response.data.message);
        // Remove the user message
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
        loadCredits(); // Refresh credits
        return;
      }
      
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update credits from response
      if (response.data.credits_remaining !== undefined) {
        setCredits({ balance: response.data.credits_remaining, loading: false });
      } else {
        loadCredits(); // Refresh credits
      }
      
      // Update session ID if new
      if (!sessionId && response.data.session_id) {
        setSessionId(response.data.session_id);
        loadSessions(); // Refresh sessions list
      }
    } catch (error) {
      console.error('AI error:', error);
      toast.error("Erreur de l'assistant. Veuillez réessayer.");
      // Remove the user message if there was an error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-slate-50" data-testid="ai-assistant-page">
      {/* Sidebar - Sessions History */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white border-r transform transition-transform duration-300
        md:relative md:translate-x-0
        ${showSidebar ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Conversations</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSidebar(false)}
              className="md:hidden"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="p-3">
            <Button
              onClick={startNewSession}
              className="w-full bg-[#E53935] hover:bg-[#C62828] text-white rounded-xl"
              data-testid="new-conversation-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle conversation
            </Button>
          </div>
          
          <ScrollArea className="flex-1 px-3">
            {loadingSessions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-8">
                Aucune conversation
              </p>
            ) : (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className={`
                      p-3 rounded-xl cursor-pointer transition-colors group
                      ${sessionId === session.session_id 
                        ? 'bg-[#E53935]/10 border border-[#E53935]/20' 
                        : 'hover:bg-slate-100'}
                    `}
                    data-testid={`session-${session.session_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {session.preview}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <Clock className="w-3 h-3" />
                          {new Date(session.created_at).toLocaleDateString('fr-FR')}
                          <span>•</span>
                          <span>{session.message_count} messages</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => deleteSession(session.session_id, e)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
      
      {/* Overlay for mobile */}
      {showSidebar && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSidebar(true)}
              className="md:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1976D2] to-[#7C3AED] flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Assistant IA Santé</h1>
              <p className="text-xs text-slate-500">Posez vos questions santé</p>
            </div>
          </div>
          
          {/* Credits Balance */}
          <Button
            variant="outline"
            onClick={() => setShowCreditsModal(true)}
            className={`flex items-center gap-2 rounded-xl ${
              credits.balance < 3 ? 'border-red-300 bg-red-50 text-red-700' : 'border-[#1A237E]/20 bg-[#E8EAF6]'
            }`}
            data-testid="credits-balance-btn"
          >
            <Coins className={`w-4 h-4 ${credits.balance < 3 ? 'text-red-500' : 'text-[#1A237E]'}`} />
            {credits.loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <span className={`font-semibold ${credits.balance < 3 ? 'text-red-700' : 'text-[#1A237E]'}`}>
                {credits.balance}
              </span>
            )}
            <span className="hidden sm:inline text-xs text-slate-500">crédits</span>
            {credits.balance < 3 && (
              <ShoppingCart className="w-4 h-4 text-red-500" />
            )}
          </Button>
        </div>

        {/* Messages Area */}
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            // Empty state with suggestions
            <div className="max-w-2xl mx-auto py-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1976D2] to-[#7C3AED] flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  Bonjour {user?.full_name?.split(' ')[0]} !
                </h2>
                <p className="text-slate-500 text-sm">
                  Je suis votre assistant santé. Posez-moi vos questions ci-dessous.
                </p>
              </div>
              
              {/* Suggested Questions - Smaller */}
              <div className="mb-4">
                <p className="text-xs text-slate-400 mb-2 text-center">Ou essayez une suggestion :</p>
                <div className="grid grid-cols-2 gap-2">
                  {SUGGESTED_QUESTIONS.map((q, index) => (
                    <button
                      key={index}
                      onClick={() => sendMessage(q.text)}
                      className="p-3 rounded-xl border border-slate-200 hover:border-[#E53935] hover:bg-red-50 transition-all text-left"
                      data-testid={`suggestion-${index}`}
                    >
                      <div className="flex items-start gap-2">
                        <q.icon className="w-4 h-4 text-[#E53935] mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-600 line-clamp-2">{q.text}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Disclaimer */}
              <div className="bg-amber-50 rounded-xl p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  <strong>Important :</strong> Cet assistant ne remplace pas une consultation médicale. 
                  En cas d&apos;urgence, appelez le 1510 (SAMU).
                </p>
              </div>
            </div>
          ) : (
            // Messages list
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`
                      max-w-[85%] rounded-2xl px-4 py-3
                      ${message.role === 'user'
                        ? 'bg-[#E53935] text-white rounded-br-sm'
                        : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm shadow-sm'}
                    `}
                  >
                    {message.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                        <Brain className="w-4 h-4 text-[#1976D2]" />
                        <span className="text-xs font-medium text-[#1976D2]">Assistant DOCKHTA</span>
                      </div>
                    )}
                    {/* Show attachment badge if present */}
                    {message.hasAttachment && (
                      <div className={`flex items-center gap-2 mb-2 px-2 py-1 rounded-lg ${
                        message.role === 'user' ? 'bg-white/20' : 'bg-slate-100'
                      }`}>
                        {message.attachmentType?.startsWith('image/') ? (
                          <Image className="w-3 h-3" />
                        ) : message.attachmentType?.startsWith('audio/') ? (
                          <Mic className="w-3 h-3" />
                        ) : (
                          <File className="w-3 h-3" />
                        )}
                        <span className="text-xs truncate max-w-[150px]">{message.attachmentName}</span>
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <p className={`text-xs mt-2 ${message.role === 'user' ? 'text-white/70' : 'text-slate-400'}`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#1976D2]" />
                      <span className="text-sm text-slate-500">En train de réfléchir...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Input Area */}
        <div className="bg-white border-t p-4">
          <div className="max-w-3xl mx-auto">
            {/* Hidden file inputs */}
            <input
              type="file"
              ref={imageInputRef}
              onChange={(e) => handleFileSelect(e, 'image')}
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileSelect(e, 'file')}
              accept="application/pdf,audio/mpeg,audio/mp3,audio/wav,audio/webm"
              className="hidden"
            />
            
            {/* Selected file preview */}
            {selectedFile && (
              <div className="mb-3 bg-slate-100 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedFile.type.startsWith('image/') ? 'bg-blue-100' :
                    selectedFile.type.startsWith('audio/') ? 'bg-purple-100' : 'bg-orange-100'
                  }`}>
                    {getFileIcon(selectedFile)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {selectedFile.type.startsWith('audio/') ? '5 crédits' : '3 crédits'} • {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={removeSelectedFile}
                  className="text-slate-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex items-center gap-2"
            >
              {/* Attachment button */}
              <div className="relative">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setShowAttachMenu(!showAttachMenu)}
                  disabled={loading}
                  className="h-12 w-12 rounded-xl border-slate-200"
                  data-testid="attach-btn"
                >
                  <Paperclip className="w-5 h-5 text-slate-500" />
                </Button>
                
                {/* Attachment menu */}
                {showAttachMenu && (
                  <div className="absolute bottom-14 left-0 bg-white rounded-xl shadow-lg border p-2 min-w-[180px] z-10">
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100 rounded-lg text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Image className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Image</p>
                        <p className="text-xs text-slate-500">3 crédits</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        fileInputRef.current?.setAttribute('accept', 'application/pdf');
                        fileInputRef.current?.click();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100 rounded-lg text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Document PDF</p>
                        <p className="text-xs text-slate-500">3 crédits</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        fileInputRef.current?.setAttribute('accept', 'audio/mpeg,audio/mp3,audio/wav,audio/webm');
                        fileInputRef.current?.click();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-100 rounded-lg text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Mic className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Audio</p>
                        <p className="text-xs text-slate-500">5 crédits</p>
                        <p className="text-xs text-amber-600">Bientôt disponible</p>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={selectedFile ? "Décrivez ce que vous souhaitez analyser..." : "Posez votre question santé..."}
                disabled={loading}
                className="flex-1 h-12 rounded-xl border-slate-200"
                data-testid="ai-input"
              />
              <Button
                type="submit"
                disabled={loading || (!input.trim() && !selectedFile) || credits.balance < 1}
                className="h-12 w-12 rounded-xl bg-[#E53935] hover:bg-[#C62828]"
                data-testid="ai-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
            
            {/* Low credits warning */}
            {credits.balance < 3 && credits.balance > 0 && (
              <div className="mt-3 bg-amber-50 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    Plus que {credits.balance} crédit{credits.balance > 1 ? 's' : ''} !
                  </span>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowCreditsModal(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  Recharger
                </Button>
              </div>
            )}
            
            {/* No credits warning */}
            {credits.balance === 0 && !credits.loading && (
              <div className="mt-3 bg-red-50 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">
                    Crédits épuisés. Rechargez pour continuer.
                  </span>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => setShowCreditsModal(true)}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="recharge-btn"
                >
                  Acheter des crédits
                </Button>
              </div>
            )}
            
            <p className="text-xs text-center text-slate-400 mt-2">
              L&apos;assistant IA ne pose pas de diagnostic. Consultez un professionnel pour tout problème de santé.
            </p>
          </div>
        </div>
      </div>
      
      {/* Credits Modal */}
      <CreditsModal 
        open={showCreditsModal} 
        onOpenChange={setShowCreditsModal}
        onPurchaseComplete={loadCredits}
      />
    </div>
  );
};

export default AIAssistantPage;
